import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "../styles/Ferrypass.css";
import NewBookingModal from "../components/NewBookingModal";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx"

const API_BASE_URL = "https://client-management-backend-8x91.onrender.com";


const FerryPass = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]); // Store vehicle types
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/bookings`);
      setBookings(response.data); // ✅ Updates the booking list
    } catch (error) {
      console.error("❌ Error fetching bookings:", error);
    }
  };
  
  const fetchVehicleTypes = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/vehicle-types`);
      setVehicleTypes(response.data); // Store vehicle types in state
    } catch (error) {
      console.error("❌ Error fetching vehicle types:", error);
    }
  };
  

  // Fetch bookings when the component mounts
  useEffect(() => {
    fetchBookings();
  }, []);

  // Handle search input
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  // Handle new booking save
  const handleNewBookingSave = async () => {
    await fetchBookings(); // Refresh table after a new booking is added
  };

  // Fetch both bookings and vehicle types when the component mounts
  useEffect(() => {
    fetchBookings();
    fetchVehicleTypes(); // Fetch vehicle types
  }, []);
  
  // Handle Status Change (Booking)
  const handleBookingStatusChange = async (id, value) => {
    try {
      const response = await axios.put(`${API_BASE_URL}/bookings/${id}/status`, {
        bookingStatus: value, // ✅ Sends only the bookingStatus field
      });
  
      // Update state with the new status
      setBookings((prevBookings) =>
        prevBookings.map((booking) =>
          booking.id === id ? { ...booking, booking_status: response.data.booking_status } : booking
        )
      );
    } catch (error) {
      console.error("❌ Error updating booking status:", error);
    }
  };
  
  const handlePaymentStatusChange = async (id, value) => {
    try {
      const response = await axios.put(`${API_BASE_URL}/bookings/${id}/status`, {
        paymentStatus: value, // ✅ Sends only the paymentStatus field
      });
  
      // Update state with the new status
      setBookings((prevBookings) =>
        prevBookings.map((booking) =>
          booking.id === id ? { ...booking, payment_status: response.data.payment_status } : booking
        )
      );
    } catch (error) {
      console.error("❌ Error updating payment status:", error);
    }
  };

  const getStatusColor = (status, type) => {
    if (type === "booking") {
      return status === "Confirmed" ? "green" :
             status === "Pending" ? "orange" :
             status === "Cancelled" ? "red" : "black";
    }
    if (type === "payment") {
      return status === "Paid" ? "green" :
             status === "Outstanding" ? "red" : "black";
    }
    return "black";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A"; // Handle missing date
    return new Date(dateString).toISOString().split("T")[0]; // Extract only YYYY-MM-DD
  };
  
  const handleDeleteBooking = async (id) => {
    if (!window.confirm("⚠️ Are you sure you want to delete this booking?")) return;
  
    try {
      await axios.delete(`${API_BASE_URL}/bookings/${id}`);
  
      setBookings((prevBookings) => prevBookings.filter((booking) => booking.id !== id));
    } catch (error) {
      console.error("❌ Error deleting booking:", error);
      alert("❌ Failed to delete booking. Please try again.");
    }
  };

const filteredBookings = bookings.filter((booking) => {
  const searchLower = searchQuery.toLowerCase();
  const matchesSearch = booking.customer_name?.toLowerCase().includes(searchLower);

  const createdDate = booking.created_at ? new Date(booking.created_at) : null;
  const start = startDate ? new Date(`${startDate}T00:00:00.000Z`) : null;
  const end = endDate ? new Date(`${endDate}T23:59:59.999Z`) : null;

  const createdTime = createdDate?.getTime();
  const startTime = start?.getTime();
  const endTime = end?.getTime();

  const matchesDate = createdDate &&
    (!startTime || createdTime >= startTime) &&
    (!endTime || createdTime <= endTime);

  return matchesSearch && matchesDate;
});

  
  //Exporting Functions
  const exportToPDF = () => {
    const doc = new jsPDF();
  
    // Display the report title
    doc.text("FerryPass Booking Report", 20, 10);
  
    // Display the reporting date range
    const formattedStartDate = startDate ? formatDate(startDate) : "N/A";
    const formattedEndDate = endDate ? formatDate(endDate) : "N/A";
    doc.text(`Reporting Period: ${formattedStartDate} to ${formattedEndDate}`, 20, 20);
  
    // Hardcode Mandatory Charge
    const MANDATORY_CHARGE = 120.00;
  
    // Calculate Totals
    const totalNetCost = filteredBookings.reduce((sum, b) => sum + parseFloat(b.net_cost || 0), 0);
    const totalMandatoryCharge = filteredBookings.length * MANDATORY_CHARGE;
    const totalAdminCharge = filteredBookings.reduce((sum, b) => sum + parseFloat(b.admin_charge || 0), 0);
  
    // Map table data
    const tableData = filteredBookings.map((booking) => [
      booking.customer_name || "Unknown",
      booking.vehicle_number || "N/A",
      booking.booking_number || "N/A",
      booking.booking_status || "Pending",
      booking.payment_status || "Outstanding",
      `$${parseFloat(booking.net_cost || 0).toFixed(2)}`,
      `$${MANDATORY_CHARGE.toFixed(2)}`, // Hardcoded Mandatory Charge
      `$${parseFloat(booking.admin_charge || 0).toFixed(2)}`,
      formatDate(booking.travel_date),
    ]);
  
    // Add totals row
    tableData.push([
      "Total",
      "",
      "",
      "",
      "",
      `$${totalNetCost.toFixed(2)}`,
      `$${totalMandatoryCharge.toFixed(2)}`,
      `$${totalAdminCharge.toFixed(2)}`,
      "",
    ]);
  
    doc.autoTable({
      head: [
        [
          "Customer",
          "Vehicle Number",
          "Booking Number",
          "Booking Status",
          "Payment Status",
          "Net Cost",
          "Mandatory Charge",
          "Admin Charge",
          "Travel Date",
        ],
      ],
      body: tableData,
      startY: 30,
      theme: "grid",
      styles: { fontSize: 8 },
      didParseCell: (data) => {
        // Style totals row
        if (data.row.index === tableData.length - 1) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.textColor = [255, 0, 0]; // Red color for totals
        }
      },
    });
  
    doc.save("FerryPass_Report.pdf");
  };
  
  
  const exportToExcel = () => {
    // Hardcode Mandatory Charge
    const MANDATORY_CHARGE = 120.00;
  
    // Calculate Totals
    const totalNetCost = filteredBookings.reduce((sum, b) => sum + parseFloat(b.net_cost || 0), 0);
    const totalMandatoryCharge = filteredBookings.length * MANDATORY_CHARGE;
    const totalAdminCharge = filteredBookings.reduce((sum, b) => sum + parseFloat(b.admin_charge || 0), 0);
  
    // Map data with added columns
    const data = filteredBookings.map((booking) => ({
      Customer: booking.customer_name || "Unknown",
      "Vehicle Number": booking.vehicle_number || "N/A",
      "Booking Number": booking.booking_number || "N/A",
      "Booking Status": booking.booking_status || "Pending",
      "Payment Status": booking.payment_status || "Outstanding",
      "Net Cost": `$${parseFloat(booking.net_cost || 0).toFixed(2)}`,
      "Mandatory Charge": `$${MANDATORY_CHARGE.toFixed(2)}`,
      "Admin Charge": `$${parseFloat(booking.admin_charge || 0).toFixed(2)}`,
      "Travel Date": formatDate(booking.travel_date),
    }));
  
    // Add totals row
    data.push({
      Customer: "Total",
      "Vehicle Number": "",
      "Booking Number": "",
      "Booking Status": "",
      "Payment Status": "",
      "Net Cost": `$${totalNetCost.toFixed(2)}`,
      "Mandatory Charge": `$${totalMandatoryCharge.toFixed(2)}`,
      "Admin Charge": `$${totalAdminCharge.toFixed(2)}`,
      "Travel Date": "",
    });
  
    // Generate Excel
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "FerryPass Report");
  
    // Add the reporting period
    const formattedStartDate = startDate ? formatDate(startDate) : "N/A";
    const formattedEndDate = endDate ? formatDate(endDate) : "N/A";
    XLSX.utils.sheet_add_aoa(ws, [["FerryPass Booking Report"]], { origin: "A1" });
    XLSX.utils.sheet_add_aoa(ws, [[`Reporting Period: ${formattedStartDate} to ${formattedEndDate}`]], { origin: "A2" });
  
    XLSX.writeFile(wb, "FerryPass_Report.xlsx");
  };
  

  return (
    <div className="ferry-pass-container">
      {/* 🔎 Search Bar & "New Booking" Button */}
      <div className="header">
        <input
          type="text"
          placeholder="Search..."
          value={searchQuery}
          onChange={handleSearch}
        />
        <button className="new-booking-btn" onClick={() => setIsModalOpen(true)}>
          + New Booking
        </button>
      </div>

      {/* 🏷️ Navigation Tabs */}
      <div className="nav-tabs">
  <button className="nav-tab" onClick={() => navigate("/customers")}>Customers</button>
  <button className="nav-tab" onClick={() => navigate("/vehicle-types")}>Vehicle Types</button>
  <button className="nav-tab" onClick={() => navigate("/vehicle-numbers")}>Vehicle Numbers</button>
  <button className="nav-tab" onClick={() => navigate("/passenger-types")}>Passenger Types</button>
</div>

<div className="reporting-section">
  <h4>Select Date Range for Report</h4>
  <label>Start Date: </label>
  <input
  type="date"
  className="date-picker"
  value={startDate}
  onChange={(e) => setStartDate(e.target.value)}
/>

<input
  type="date"
  className="date-picker"
  value={endDate}
  onChange={(e) => setEndDate(e.target.value)}
/>

<button onClick={exportToPDF} className="export-btn pdf-btn">
  Export to PDF
</button>

<button onClick={exportToExcel} className="export-btn excel-btn">
  Export to Excel
</button>

</div>


      {/* 📋 Main Ferry Pass Table */}
      <div className="ferry-pass-table">
        <table>
          <thead>
            <tr>
              <th>Customer</th>
              <th>Vehicle Number</th>
              <th>Vehicle Type</th>
              <th>Booking Number</th>
              <th>Booking Status</th>
              <th>Payment Number</th>
              <th>Payment Status</th>
              <th>Passenger Details</th>
              <th>Mode</th>
              <th>Travel Date</th>
              <th>Mandatory Charge</th>
              <th>Admin Charge</th>
              <th>Net Cost</th>
              <th>Date Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
          {filteredBookings.map((booking, index) => (

                <tr key={index}>
                  <td>{booking.customer_name || "Unknown"}</td>
                  <td>{booking.vehicle_number || "N/A"}</td>
                  <td>
  {vehicleTypes.find(v => v.id.toString() === booking.vehicle_type.toString())?.name || "Unknown"}
</td>

                  <td>{booking.booking_number || "N/A"}</td>
                  <td>
          <select
            value={booking.booking_status || "Pending"}
            onChange={(e) => handleBookingStatusChange(booking.id, e.target.value)}
            style={{ color: getStatusColor(booking.booking_status, "booking") }}
          >
            <option value="Confirmed" style={{ color: "green" }}>Confirmed</option>
            <option value="Pending" style={{ color: "orange" }}>Pending</option>
            <option value="Cancelled" style={{ color: "red" }}>Cancelled</option>
          </select>
        </td>
        <td>{booking.payment_number || "N/A"}</td>
        {/* Payment Status with Color */}
        <td>
          <select
            value={booking.payment_status || "Outstanding"}
            onChange={(e) => handlePaymentStatusChange(booking.id, e.target.value)}
            style={{ color: getStatusColor(booking.payment_status, "payment") }}
          >
            <option value="Paid" style={{ color: "green" }}>Paid</option>
            <option value="Outstanding" style={{ color: "red" }}>Outstanding</option>
          </select>
        </td>
                  <td>
                    {Object.entries(booking.passengers || {}).map(([type, count]) => (
                      <span key={type}>{type}: {count} </span>
                    ))}
                  </td>
                  <td>{booking.mode_of_travel || "One Way"}</td>
                  <td>{formatDate(booking.travel_date)}</td>
                  <td>$120</td> {/* Mandatory Charge */}
                  <td>${booking.admin_charge || 0}</td>
                  <td>${booking.net_cost || 0}</td>
                  <td>{booking.created_at ? new Date(booking.created_at).toLocaleDateString() : "N/A"}</td>
                  <td>
                    <button>Edit</button>
                    <td>
  <button 
    onClick={() => handleDeleteBooking(booking.id)} 
    style={{ backgroundColor: "red", color: "white", border: "none", padding: "5px 10px", cursor: "pointer" }}
  >
    Delete
  </button>
</td>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* 🎉 New Booking Modal Integration */}
      {isModalOpen && (
        <NewBookingModal
  isOpen={isModalOpen}
  onClose={() => setIsModalOpen(false)}
  fetchBookings={fetchBookings} // ✅ Ensures the table updates after booking
/>

      )}
    </div>
  );
};

export default FerryPass;
